import { useEffect, useState } from 'react';
import { Home } from 'lucide-react';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';
import { STANDARD_VIEWS, VIEW_LABELS, type StandardView } from '@/viewer/standardViews';
import { cssMatrix3d, cubeRotationMatrix } from './cubeTransform';
import { cn } from '@/lib/cn';

const SIZE = 72;
const HALF = SIZE / 2;
const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/**
 * Face placement in CSS space, where +Y points down. Each face is pushed out
 * along its own +Z after rotating, so its text reads correctly from outside;
 * `backface-visibility: hidden` then hides the far faces, which is what makes
 * plain DOM clicks pick the face you can actually see.
 */
const FACES: { view: StandardView; transform: string }[] = [
  { view: 'back', transform: `translateZ(${HALF}px)` },
  { view: 'front', transform: `rotateY(180deg) translateZ(${HALF}px)` },
  { view: 'right', transform: `rotateY(90deg) translateZ(${HALF}px)` },
  { view: 'left', transform: `rotateY(-90deg) translateZ(${HALF}px)` },
  { view: 'top', transform: `rotateX(90deg) translateZ(${HALF}px)` },
  { view: 'bottom', transform: `rotateX(-90deg) translateZ(${HALF}px)` },
];

export function ViewCube() {
  const { setStandardView, getViewer } = useViewer();
  const modelName = useViewerStore((s) => s.modelName);
  const hasSelection = useViewerStore((s) => s.selection.expressID !== null);
  const [transform, setTransform] = useState(() => cssMatrix3d(IDENTITY));

  useEffect(() => {
    const viewer = getViewer();
    if (!viewer) return;
    const sync = (): void => {
      setTransform(cssMatrix3d(cubeRotationMatrix(viewer.camera.quaternion)));
    };
    sync();
    viewer.onCameraChange(sync);
    return () => viewer.onCameraChange(null);
  }, [getViewer, modelName]);

  if (modelName === null) return null;

  return (
    <div
      data-testid="view-cube-root"
      role="group"
      aria-label="View cube"
      className={cn(
        'pointer-events-auto absolute top-16 z-30 flex flex-col items-center gap-1.5 transition-[right]',
        // the properties panel owns the right edge whenever something is picked
        hasSelection ? 'right-[21.5rem]' : 'right-3',
      )}
    >
      <div style={{ width: SIZE, height: SIZE }}>
        <div
          data-testid="view-cube"
          className="relative h-full w-full"
          style={{ transformStyle: 'preserve-3d', transform }}
        >
          {FACES.map(({ view, transform: faceTransform }) => (
            <button
              key={view}
              type="button"
              aria-label={`${VIEW_LABELS[view]} view`}
              title={`${VIEW_LABELS[view]} view`}
              onClick={() => setStandardView(view)}
              className="absolute inset-0 flex items-center justify-center rounded-sm border border-accent/40 bg-panel/85 text-[10px] font-semibold tracking-wide text-foreground/90 uppercase transition-colors hover:bg-accent/30 hover:text-accent"
              style={{ transform: faceTransform, backfaceVisibility: 'hidden' }}
            >
              {VIEW_LABELS[view]}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        aria-label={`${VIEW_LABELS.iso} view`}
        title="Iso view"
        onClick={() => setStandardView('iso')}
        className="inline-flex h-6 items-center gap-1 rounded-md border border-panel-border bg-panel/85 px-2 text-[10px] font-medium text-muted transition-colors hover:text-foreground"
      >
        <Home className="h-3 w-3" />
        {VIEW_LABELS.iso}
      </button>
    </div>
  );
}

export { STANDARD_VIEWS };
